import { useState, useMemo, useCallback, useEffect } from 'react';
import './varianteProduit.scss';
import './stockBadges.css';
import '../../components/ndoe/ndoeCatalogue.css';
import { LeftOutlined, RightOutlined, ShoppingCartOutlined, PlusOutlined, AppstoreOutlined, PicCenterOutlined } from '@ant-design/icons';
import { Drawer, Input, InputNumber, Modal, Segmented, Select, Tag, Button, Space, message, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import ReactPaginate from 'react-paginate';
import PageDetails from '../PageDetails/PageDetails';
import ReservationPanel from './ReservationPanel';
import PanierBar from './PanierBar';
import { useVarianteProduit } from './hooks/useVarianteProduit';
import FilterSelect from './composant/filterSelect/FilterSelect';
import QuickAddSheet from './QuickAddSheet';
import { NdoePage, NdoePageHeader, NdoeImage, NdoeLoading, NdoeEmpty, NdoeActionButton, useIsDesktop } from '../../components/ndoe';

const ITEMS_PER_PAGE = 12;

const VarianteProduit = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [filters, setFilters] = useState({
    matiere: [],
    couleur: [],
    famille: [],
    marque: [],
    cible: [],
    taille: [],
    talon: [],
    criteres: [],
  });

  const [currentPage, setCurrentPage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null); // ⑪ produit de la carte ouverte
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  // ⑪ Quick-add : bottom-sheet de pointures + densité de grille (1/2 col mobile).
  const [sheetProduct, setSheetProduct] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dense, setDense] = useState(false); // false = grille normale, true = 1 grande colonne
  const nbFiltresActifs = Object.values(filters).reduce((n, v) => n + (v?.length || 0), 0);

  // Vérité stock (Lot A) : enrichissement des variantes déjà chargées.
  const [stockMap, setStockMap] = useState({});
  // Chantier #4 : par défaut on ne montre que le disponible (« comme un magasin ») ;
  // « Tout voir » révèle ruptures/indispo. stockLoading évite d'afficher un écran
  // vide tant que la vérité stock (by-ids) n'est pas revenue.
  const [stockFilter, setStockFilter] = useState('disponible');
  const [stockLoading, setStockLoading] = useState(true);
  const [stockError, setStockError] = useState(false); // by-ids en échec → fail-open
  const [search, setSearch] = useState(''); // recherche libre (Lot B)
  const [tri, setTri] = useState(''); // tri décisionnel (ventes / arrivages) — serveur
  const role = useSelector((s) => s.user?.currentUser?.role);
  const isAdmin = role === 'admin';
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const {
    getMarque: marques,
    getCible: cibles,
    getTaille: tailles,
    getMatiere: matieres,
    getFamille: familles,
    getCouleur: couleurs,
    data: rawData,
    loading,
  } = useVarianteProduit({ ...filters, sort: tri });

  /*
   * LES NIVEAUX DE TALON — chargés à part, et sans jamais bloquer.
   *
   * Le hook du catalogue rend les référentiels d'un bloc ; celui-ci vit sur
   * une autre route. En cas d'échec la liste reste vide, la facette ne
   * s'affiche pas, et le catalogue fonctionne exactement comme avant — un
   * filtre absent vaut mieux qu’un menu vide et muet.
   */
  const [talons, setTalons] = useState([]);
  /*
   * LES CRITÈRES DU MODÈLE, pour les facettes.
   *
   * Chargés une fois : c'est un référentiel, pas une donnée de session. En
   * cas d'échec la liste reste vide et AUCUNE facette ne s'affiche — un
   * filtre absent vaut mieux qu'un menu vide et muet.
   */
  const [criteres, setCriteres] = useState([]);
  useEffect(() => {
    let vivant = true;
    axios.get(`${DOMAIN}/api/produit/attributs`)
      .then(({ data }) => { if (vivant) setCriteres(Array.isArray(data && data.attributs) ? data.attributs : []); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [DOMAIN]);
  useEffect(() => {
    let vivant = true;
    axios.get(`${DOMAIN}/api/produit/hauteur-talon/niveaux`)
      .then(({ data }) => { if (vivant) setTalons(Array.isArray(data && data.niveaux) ? data.niveaux : []); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [DOMAIN]);

  // Enrichit les variantes affichées avec la vérité stock (sans changer la source).
  useEffect(() => {
    const ids = [...new Set((rawData || []).map((r) => r.id_varianteProduit).filter(Boolean))];
    if (!ids.length) { setStockMap({}); setStockLoading(false); setStockError(false); return undefined; }
    let cancelled = false;
    setStockLoading(true);
    setStockError(false);
    (async () => {
      try {
        const { data } = await axios.post(`${DOMAIN}/api/catalogue/stock/by-ids`, { ids });
        if (cancelled) return;
        const map = {};
        (data.variantes || []).forEach((v) => { map[v.id_varianteProduit] = v; });
        setStockMap(map);
      } catch (e) {
        // Enrichissement en échec : on bascule en « fail-open » (matchStock montre
        // tout) pour ne pas vider la galerie sous le filtre « En stock » par défaut.
        if (!cancelled) setStockError(true);
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rawData, DOMAIN]);

  // Navigation croisée (Lot E) : pré-remplit la recherche depuis ?q= (ouverture
  // depuis la Liste Produits).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) setSearch(q);
  }, []);

  const { paginatedData, totalPages } = useMemo(() => {
    // Map (et non Object.values) : préserve l'ordre serveur du tri même si un
    // code_variant est purement numérique (Object.values réordonnerait les clés
    // entières en tête → tri décisionnel cassé).
    const gmap = new Map();
    rawData.forEach((item) => {
      const { code_variant, ...rest } = item;
      if (!gmap.has(code_variant)) gmap.set(code_variant, { code_variant, data: [] });
      gmap.get(code_variant).data.push(rest);
    });
    const grouped = [...gmap.values()];

    // ⚠ UNE CARTE = UN GROUPE, PAS UNE VARIANTE (corrigé le 31/07/2026).
    //
    // Signalé par le propriétaire : « l'écran affiche un modèle mais affiche le
    // disponible apparemment de la variante vedette ».
    //
    // C'était exact. La carte ne portait que `group.data[0]` : le stock lu était
    // celui de la PREMIÈRE pointure du groupe. Mesuré sur ESC-BER-0001, dont le
    // groupe « Noir brillant » compte 4 pointures — la carte affichait 20, le
    // total réel du groupe était 78.
    //
    // Le défaut d'AFFICHAGE n'était pas le pire. Le filtre « En stock » lisait
    // la même valeur : un coloris dont la première pointure était épuisée
    // DISPARAISSAIT de la galerie, même avec vingt paires dans les autres
    // pointures. Du stock vendable, invisible.
    //
    // On garde donc la liste des variantes du groupe, et tout ce qui est chiffré
    // se calcule dessus.
    const firstItems = grouped.map((group) => ({
      ...group.data[0],
      code_variant: group.code_variant,
      idsGroupe: group.data.map((v) => v.id_varianteProduit).filter(Boolean),
    }));

    // Vérité stock AGRÉGÉE du groupe. `null` tant qu'aucune variante n'est
    // enrichie — on ne devine pas un stock qu'on n'a pas reçu.
    const veriteDuGroupe = (item) => {
      const ids = item.idsGroupe?.length ? item.idsGroupe : [item.id_varianteProduit];
      const vs = ids.map((i) => stockMap[i]).filter(Boolean);
      if (!vs.length) return null;
      const somme = (cle) => vs.reduce((s, v) => s + (Number(v[cle]) || 0), 0);
      return {
        disponible: somme('disponible'),
        physique: somme('physique'),
        reservee: somme('reservee') + somme('reserve_commerciale'),
        en_circulation: somme('en_circulation'),
        avariees: somme('avariees'),
        // Un seul coloris déconseillé suffit à signaler le groupe : c'est une
        // alerte, elle doit remonter, pas se diluer dans la moyenne.
        deconseille: vs.some((v) => v.deconseille),
        nbPointures: vs.length,
      };
    };

    // Filtre « vérité stock » (pastilles). Il porte sur le TOTAL du groupe —
    // sinon il masque du stock réellement vendable (voir plus haut).
    const matchStock = (item) => {
      if (stockFilter === 'tous') return true;
      if (stockError) return true; // enrichissement HS : ne rien masquer
      const t = veriteDuGroupe(item);
      if (!t) return false;
      if (stockFilter === 'disponible') return t.disponible > 0;
      if (stockFilter === 'rupture') return t.disponible <= 0;
      if (stockFilter === 'deconseille') return !!t.deconseille;
      if (stockFilter === 'avarie') return t.avariees > 0;
      return true;
    };
    // Recherche libre (Lot B) : nom, marque, couleur, pointure.
    const q = search.trim().toLowerCase();
    const matchSearch = (item) => !q
      || [item.nom_produit, item.marque, item.couleur, item.taille]
        .some((f) => String(f ?? '').toLowerCase().includes(q));

    const filtered = firstItems.filter((item) => matchStock(item) && matchSearch(item));

    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    // Le total du groupe voyage AVEC la carte : le rendu ne doit pas avoir à le
    // recalculer, ni pouvoir retomber par distraction sur une seule variante.
    const paginated = filtered.slice(startIndex, endIndex)
      .map((item) => ({ ...item, verite: veriteDuGroupe(item) }));
    const pages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

    return {
      groupedData: grouped,
      paginatedData: paginated,
      totalPages: pages,
    };
  }, [rawData, currentPage, stockMap, stockFilter, search, stockError]);

  // Enrichissement en cours = rawData arrivé mais vérité stock pas encore là (et pas
  // d'échec). Dérivé au rendu (pas via un useEffect qui retarde d'une frame) → évite
  // le flash « Aucune paire » avant l'arrivée des pastilles sous le filtre « En stock ».
  const enrichmentPending = (rawData || []).length > 0 && !stockError && Object.keys(stockMap).length === 0;

  // Gestionnaires d'événements
  const handleFilterChange = useCallback((filterName) => (selectedOptions) => {
    const values = selectedOptions?.map(option => option.value) || [];
    setFilters(prev => ({
      ...prev,
      [filterName]: values,
    }));
    setCurrentPage(0);
  }, []);

  const handlePageChange = useCallback(({ selected }) => {
    setCurrentPage(selected);
  }, []);

  const handleVariantClick = useCallback((product) => {
    setSelectedVariantId(product.id_varianteProduit);
    setSelectedProduct(product);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedVariantId(null);
    setSelectedProduct(null);
  }, []);

  // ⑪ Ouvre le bottom-sheet de pointures pour un modèle (quick-add).
  const openSheet = useCallback((product) => {
    setSheetProduct(product);
    setSheetOpen(true);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      matiere: [],
      talon: [],
      couleur: [],
      famille: [],
      marque: [],
      cible: [],
      taille: [],
    });
    setStockFilter('disponible');
    setSearch('');
    setCurrentPage(0);
  }, []);

  // --- Actions admin (à ne plus recommander / mauvais état) -------------------
  // Applique le changement de recommandation (partagé prompt/pas-prompt).
  const applyDeconseille = useCallback(async (idProduit, next, motif) => {
    try {
      await axios.put(`${DOMAIN}/api/catalogue/produit/${idProduit}/recommandation`, { deconseille: next, motif: motif ?? null });
      setStockMap((m) => {
        const n = { ...m };
        Object.keys(n).forEach((k) => {
          if (n[k].id_produit === idProduit) n[k] = { ...n[k], deconseille: next, motif_deconseille: next ? motif : null };
        });
        return n;
      });
      message.success(next ? 'Modèle marqué « à ne plus recommander ».' : 'Recommandation rétablie.');
    } catch (e) {
      message.error(e?.response?.data?.error || 'Action échouée.');
    }
  }, [DOMAIN]);

  const toggleDeconseille = useCallback((variantId) => {
    const t = stockMap[variantId] || {};
    const idProduit = t.id_produit;
    if (!idProduit) return;
    const next = !t.deconseille;

    if (!next) { applyDeconseille(idProduit, false, null); return; }  // rétablir : sans motif

    let motifVal = '';
    Modal.confirm({
      title: 'Ne plus recommander ce modèle',
      icon: null,
      content: (
        <Input.TextArea
          rows={2}
          placeholder="Motif (optionnel)…"
          onChange={(e) => { motifVal = e.target.value; }}
        />
      ),
      okText: 'Confirmer',
      cancelText: 'Annuler',
      onOk: () => applyDeconseille(idProduit, true, motifVal.trim() || null),
    });
  }, [stockMap, applyDeconseille]);

  const signalerAvarie = useCallback((variantId) => {
    const t = stockMap[variantId] || {};
    let paires = Number(t.avariees ?? 0);
    Modal.confirm({
      title: 'Signaler des paires en mauvais état',
      icon: null,
      content: (
        <div>
          <p style={{ marginBottom: 8, color: '#5d6b80' }}>
            Déduit du stock comptable (physique&nbsp;: {t.physique ?? '?'}).
          </p>
          <InputNumber inputMode="decimal"
            min={0}
            defaultValue={paires}
            style={{ width: '100%' }}
            onChange={(v) => { paires = Number(v); }}
          />
        </div>
      ),
      okText: 'Enregistrer',
      cancelText: 'Annuler',
      onOk: async () => {
        if (!Number.isInteger(paires) || paires < 0) {
          message.warning('Entier ≥ 0 attendu.');
          return Promise.reject(new Error('invalid'));
        }
        try {
          const { data } = await axios.post(`${DOMAIN}/api/catalogue/variante/${variantId}/avarie`, { paires });
          setStockMap((m) => {
            const v = m[variantId];
            if (!v) return m;
            const avariees = data.result.paires_avariees;
            // L'avarie décrémente le comptable (règle 2026-07) : on prend le stock
            // renvoyé par le serveur, et le disponible NE re-soustrait PLUS les avariées.
            const physique = data.result.stock_apres != null ? Number(data.result.stock_apres) : v.physique;
            return { ...m, [variantId]: { ...v, physique, avariees, disponible: Math.max(0, physique - v.reservee - v.en_circulation) } };
          });
          message.success('Mauvais état enregistré.');
          return undefined;
        } catch (e) {
          message.error(e?.response?.data?.error || 'Action échouée.');
          return Promise.reject(e);
        }
      },
    });
  }, [stockMap, DOMAIN]);

  // Rafraîchit la vérité stock d'une variante après réservation/libération.
  const refreshVariant = useCallback(async (variantId) => {
    if (!variantId) return;
    try {
      const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock/${variantId}`);
      setStockMap((m) => ({ ...m, [variantId]: { ...(m[variantId] || {}), ...data } }));
    } catch (e) {
      // non bloquant
    }
  }, [DOMAIN]);

  // Options pour les filtres Select
  const filterOptions = useMemo(() => ({
    famille: familles?.map(({ id_famille, nom }) => ({
      value: id_famille,
      label: nom
    })),
    marque: marques?.map(({ id_marque, nom }) => ({
      value: id_marque,
      label: nom
    })),
    cible: cibles?.map(({ id_cible, nom_cible }) => ({
      value: id_cible,
      label: nom_cible
    })),
    taille: tailles
      ?.sort((a, b) => a.id_taille - b.id_taille)
      .map(({ id_taille, taille }) => ({
        value: id_taille,
        label: taille
      })),
    couleur: couleurs?.map(({ id_couleur, description }) => ({
      value: id_couleur,
      label: description
    })),
    matiere: matieres?.map(({ id_matiere, nom_matiere }) => ({
      value: id_matiere,
      label: nom_matiere
    })),
    // HAUTEUR DE TALON. « Non renseigné » en tête : tant que le critère se
    // remplit, c'est la vue utile — elle montre les modèles qui restent à
    // qualifier, pas ceux qui le sont déjà.
    // Les critères, groupés par nature : le menu montre « Ouverture » puis ses
    // valeurs. C'est ce qui permet UNE facette au lieu de quatre sur une barre
    // de filtres déjà chargée.
    criteres: (criteres || []).map((a) => ({
      label: a.libelle,
      options: (a.valeurs || []).map((v) => ({ value: v.id_valeur, label: v.libelle })),
    })),
    talon: (talons || []).length
      ? [{ value: 'vide', label: 'Non renseigné' },
         ...talons.map(({ id_hauteur_talon, nom, fourchette }) => ({
           value: id_hauteur_talon,
           label: fourchette ? `${nom} (${fourchette})` : nom,
         }))]
      : [],
  }), [familles, marques, cibles, tailles, couleurs, matieres, talons, criteres]);

  // Valeurs actuelles des filtres
  const filterValues = useMemo(() => ({
    famille: filterOptions.famille?.filter(opt =>
      filters.famille.includes(opt.value)
    ) || [],
    marque: filterOptions.marque?.filter(opt =>
      filters.marque.includes(opt.value)
    ) || [],
    cible: filterOptions.cible?.filter(opt =>
      filters.cible.includes(opt.value)
    ) || [],
    taille: filterOptions.taille?.filter(opt =>
      filters.taille.includes(opt.value)
    ) || [],
    couleur: filterOptions.couleur?.filter(opt =>
      filters.couleur.includes(opt.value)
    ) || [],
    matiere: filterOptions.matiere?.filter(opt =>
      filters.matiere.includes(opt.value)
    ) || [],
    /*
      LES CRITÈRES SONT GROUPÉS : leurs options vivent sous un niveau de plus
      (« Ouverture » > « Ouvert derrière »). On aplatit donc avant de
      retrouver la sélection — sinon `filter` ne verrait que les en-têtes de
      groupe, et la facette paraîtrait vide alors que le filtre est actif.
    */
    criteres: (filterOptions.criteres || [])
      .flatMap((g) => (g && Array.isArray(g.options) ? g.options : [g]))
      .filter((opt) => opt && filters.criteres.includes(opt.value)),
  }), [filters, filterOptions]);

  const filterConfigs = [
    { key: 'famille', label: 'Catégorie', options: filterOptions.famille, value: filterValues.famille },
    { key: 'marque', label: 'Marque', options: filterOptions.marque, value: filterValues.marque },
    { key: 'cible', label: 'Cible', options: filterOptions.cible, value: filterValues.cible },
    { key: 'taille', label: 'Pointure', options: filterOptions.taille, value: filterValues.taille },
    { key: 'couleur', label: 'Couleur', options: filterOptions.couleur, value: filterValues.couleur },
    { key: 'matiere', label: 'Matière', options: filterOptions.matiere, value: filterValues.matiere },
    { key: 'talon', label: 'Hauteur', options: filterOptions.talon, value: filterValues.talon },
    /*
      UNE SEULE FACETTE POUR TOUS LES CRITÈRES.

      Les valeurs sont regroupées par critère dans le menu (« Ouverture »,
      « Maintien »…), mais elles voyagent dans une seule liste : le serveur
      sait à quel critère chacune appartient, l'écran n'a pas à le redire.

      Une facette par critère aurait allongé la barre de filtres de quatre
      menus supplémentaires, sur un écran déjà chargé — et la plupart
      resteraient vides tant que la qualification est en cours.
    */
    ...(criteres.length ? [{
      key: 'criteres',
      label: 'Critères',
      options: filterOptions.criteres,
      value: filterValues.criteres,
    }] : []),
  ];

  // Squelettes de chargement — mêmes cartes fantômes que partage-catalogue.
  const renderSkeletons = () => <NdoeLoading rows={8} />;

  const renderProducts = () => {
    if (paginatedData.length === 0) {
      return (
        <NdoeEmpty
          icon="👟"
          title="Aucun produit"
          text={stockFilter === 'disponible'
            ? 'Aucune paire en stock pour ces filtres. Essayez « Tout voir ».'
            : 'Aucun produit trouvé avec les filtres sélectionnés.'}
          action={<NdoeActionButton variant="secondary" onClick={clearAllFilters}>Effacer les filtres</NdoeActionButton>}
        />
      );
    }

    return (
      <div className={`ndoe-cat-grille ${dense ? 'is-1col' : ''}`}>
        {paginatedData.map((product) => {
          // Le total du GROUPE (toutes pointures), pas celui de la variante
          // vedette — c'est le défaut signalé le 31/07/2026.
          const t = product.verite;
          return (
            <div
              key={product.id_varianteProduit}
              className="ndoe-cat-carte"
              onClick={() => handleVariantClick(product)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleVariantClick(product);
                }
              }}
            >
              <div className="ndoe-cat-thumb">
                {t && (t.deconseille || t.avariees > 0) && (
                  <div className="ndoe-cat-badges">
                    {t.deconseille && <Tag color="orange">Déconseillé</Tag>}
                    {t.avariees > 0 && <Tag color="red">{t.avariees} en mauvais état</Tag>}
                  </div>
                )}
                <NdoeImage src={product.img} alt={product.nom_produit || 'Produit'} size="lg" sizes="200px" preview={false} />
                {/* ⑪ Quick-add : ouvre le bottom-sheet des pointures SANS ouvrir la
                    fiche (stopPropagation). La photo (preview={false}) ne capte rien. */}
                <button
                  type="button"
                  className="ndoe-cat-add"
                  aria-label="Ajouter au panier"
                  title="Ajouter une pointure"
                  onClick={(e) => { e.stopPropagation(); openSheet(product); }}
                >
                  <PlusOutlined />
                </button>
              </div>

              <div className="ndoe-cat-corps">
                <span className="ndoe-cat-sub">{product.marque || product.nom_marque || ' '}</span>
                <span className="ndoe-cat-nom" title={product.nom_produit}>{product.nom_produit}</span>
                <span className="ndoe-cat-ref num">{product.code_produit || product.code_variant || ' '}</span>
                <div className="ndoe-cat-bas">
                  {product.prix != null && product.prix !== '' && <span className="ndoe-cat-prix num">{product.prix} $</span>}
                  {t && (
                    <span className={`ndoe-cat-dispo ${t.disponible > 0 ? 'ok' : 'rupture'}`}>
                      <i aria-hidden="true" />
                      {t.disponible > 0 ? `${t.disponible} dispo` : 'Rupture'}
                    </span>
                  )}
                </div>
                {t && (
                  <small className="ndoe-cat-detail num">
                    {/* Le nombre de pointures rend le total lisible : « 78 dispo »
                        sans lui laisse croire à une seule pointure bien fournie. */}
                    {t.nbPointures > 1 && <>{t.nbPointures} pointures · </>}
                    phys {t.physique} · rés {t.reservee} · circ {t.en_circulation}{t.avariees > 0 ? ` · av ${t.avariees}` : ''}
                  </small>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Catalogue"
        title="Notre catalogue"
        subtitle="Par défaut, seules les paires en stock s'affichent — « Tout voir » révèle le reste."
        actions={(
          <>
            <Input.Search
              allowClear
              placeholder="Rechercher (nom, marque, couleur, pointure)…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(0); }}
              style={{ maxWidth: 360 }}
            />
            {/* L'entrée du nouveau parcours vit ICI, dans l'en-tête du catalogue —
                là où l'on est quand on constate qu'un modèle manque. L'ancien
                chemin passait par un sous-menu « Créer un produit », puis deux
                autres écrans. */}
            {isAdmin && (
              <NdoeActionButton variant="primary" onClick={() => navigate('/catalogue/nouveau')}>
                + Nouveau modèle
              </NdoeActionButton>
            )}
          </>
        )}
      />
      <div className="varianteProduit vp-catalogue">
        <div className="varianteProduit-wrapper">
        <div className="variant-container-bottom">
          {/* Filtres repliés par défaut en mobile : sinon 6 selects empilés
              masquent les produits sous la ligne de flottaison. */}
          <button
            type="button"
            className="vp-filtres-toggle"
            onClick={() => setFiltresOuverts((v) => !v)}
            aria-expanded={filtresOuverts}
          >
            Filtres{nbFiltresActifs > 0 ? ` (${nbFiltresActifs})` : ''} {filtresOuverts ? '▲' : '▼'}
          </button>
          <section
            className={`variant_top vp-filtres ${filtresOuverts ? 'is-open' : ''}`}
            aria-label="Filtres de produits"
          >
            {filterConfigs.map((config) => (
              <FilterSelect
                key={config.key}
                filterKey={config.key}
                label={config.label}
                options={config.options}
                value={config.value}
                onChange={handleFilterChange(config.key)}
                placeholder={`Sélectionner...`}
              />
            ))}
          </section>

          {/* Filtre vérité stock (Lot A) + tri + densité — barre COLLANTE (⑪, sticky
              possible depuis ⑩ qui a retiré overflow:hidden de .appOutlet). */}
          <div className="vp-stickybar">
            <div className="variant_stock_filters">
              <Segmented
                value={stockFilter}
                onChange={(v) => { setStockFilter(v); setCurrentPage(0); }}
                options={[
                  { label: 'En stock', value: 'disponible' },
                  { label: 'Voir tout', value: 'tous' },
                  { label: 'Rupture', value: 'rupture' },
                  { label: 'Déconseillé', value: 'deconseille' },
                  { label: 'Mauvais état', value: 'avarie' },
                ]}
              />
              <Select
                value={tri}
                onChange={(v) => { setTri(v); setCurrentPage(0); }}
                style={{ minWidth: 210 }}
                options={[
                  { value: '', label: 'Tri : A→Z' },
                  { value: 'sales_desc', label: 'Meilleures ventes (90 j)' },
                  { value: 'last_sale', label: 'Dernières ventes' },
                  { value: 'last_receipt', label: 'Derniers arrivages' },
                ]}
              />
              {/* ⑪ Densité de la grille (surtout utile sur mobile). */}
              <Tooltip title={dense ? 'Grille compacte' : 'Grande photo'}>
                <Segmented
                  className="vp-densite"
                  value={dense ? 'un' : 'multi'}
                  onChange={(v) => setDense(v === 'un')}
                  options={[
                    { value: 'multi', icon: <AppstoreOutlined /> },
                    { value: 'un', icon: <PicCenterOutlined /> },
                  ]}
                />
              </Tooltip>
            </div>
          </div>

          {stockFilter === 'disponible' && (
            <small className="vp-stock-hint">
              « En stock » se base sur une pointure représentative par modèle — cliquez « Voir tout » si un modèle attendu n’apparaît pas.
            </small>
          )}

          {/* Section des produits */}
          <section className="variant_bottom">
            {(loading || (stockFilter !== 'tous' && (stockLoading || enrichmentPending))) ? renderSkeletons() : renderProducts()}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <ReactPaginate
                pageCount={totalPages}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageChange}
                previousLabel={<LeftOutlined aria-label="Page précédente" />}
                nextLabel={<RightOutlined aria-label="Page suivante" />}
                containerClassName="pagination"
                activeClassName="active"
                pageClassName="pagination-page"
                breakClassName="pagination-break"
                previousClassName="pagination-previous"
                nextClassName="pagination-next"
                disabledClassName="pagination-disabled"
                forcePage={currentPage}
              />
            )}

            <Drawer
              title="Détail de la paire"
              placement="right"
              width={isDesktop ? 580 : '100%'}
              open={isModalOpen}
              onClose={closeModal}
              destroyOnClose
              footer={isAdmin && selectedVariantId ? (
                <Space wrap>
                  <Button onClick={() => toggleDeconseille(selectedVariantId)}>
                    {stockMap[selectedVariantId]?.deconseille ? 'Rétablir la recommandation' : 'À ne plus recommander'}
                  </Button>
                  <Button danger onClick={() => signalerAvarie(selectedVariantId)}>
                    Signaler mauvais état
                  </Button>
                </Space>
              ) : null}
            >
              {selectedVariantId && <PageDetails id={selectedVariantId} />}
              {selectedVariantId && stockMap[selectedVariantId]?.nom_produit && (
                <Button
                  type="link"
                  style={{ paddingLeft: 0 }}
                  onClick={() => navigate(`/products?q=${encodeURIComponent(stockMap[selectedVariantId].nom_produit)}`)}
                >
                  Ouvrir dans « Produits &amp; gestion » →
                </Button>
              )}
              {selectedVariantId && selectedProduct && (
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  block
                  style={{ background: '#071c3d', borderColor: '#071c3d', marginTop: 8 }}
                  onClick={() => { const p = selectedProduct; closeModal(); openSheet(p); }}
                >
                  Choisir la pointure et ajouter
                </Button>
              )}
              {selectedVariantId && (
                <ReservationPanel variantId={selectedVariantId} onChange={() => refreshVariant(selectedVariantId)} />
              )}
            </Drawer>
          </section>
        </div>
      </div>
      <PanierBar />
      {/* ⑪ Bottom-sheet quick-add (pointures d'un modèle → panier, +1 par appui). */}
      <QuickAddSheet open={sheetOpen} product={sheetProduct} onClose={() => setSheetOpen(false)} />
      </div>
    </NdoePage>
  );
};

export default VarianteProduit;
